// Echeance reads, paginated for the "1-4 sur 24" counter, ordered by date.
import type { Echeance } from '@horizon/types';
import { getClient } from './client.js';
import { toErreur, withRetry } from './erreurs.js';

const COLONNES =
  'id,simulation_id,date_echeance,type,montant,statut,commentaire,progression_reference';

export interface PageEcheances {
  readonly lignes: readonly Echeance[];
  readonly total: number;
}

export async function listerEcheances(
  simulationId: string,
  page = 0,
  taille = 4,
): Promise<PageEcheances> {
  return withRetry(async () => {
    const debut = page * taille;
    const { data, error, count } = await getClient()
      .from('echeances')
      .select(COLONNES, { count: 'exact' })
      .eq('simulation_id', simulationId)
      .order('date_echeance', { ascending: true })
      .range(debut, debut + taille - 1);
    if (error !== null) {
      throw toErreur(error);
    }
    return { lignes: (data ?? []) as unknown as Echeance[], total: count ?? 0 };
  });
}

/** Pending statuses of the "to validate" counter: not yet settled, not rejected. */
const STATUTS_A_VALIDER: readonly string[] = ['planifie', 'en_cours'];

/** Due date needing an advisor action, resolved to its client through the simulation. */
export interface EcheanceATraiter {
  readonly id: string;
  readonly date_echeance: string;
  readonly type: Echeance['type'];
  readonly statut: Echeance['statut'];
  readonly client: {
    readonly id: string;
    readonly prenom: string;
    readonly nom: string;
  } | null;
}

/** Raw PostgREST row of `echeancesATraiter`, with its embedded simulation and client. */
interface LigneATraiterBrute {
  readonly id: string;
  readonly date_echeance: string;
  readonly type: Echeance['type'];
  readonly statut: Echeance['statut'];
  readonly simulation: {
    readonly client: { readonly id: string; readonly prenom: string; readonly nom: string } | null;
  } | null;
}

/** Number of pending due dates up to the given date included (dashboard counter). */
export async function compterEcheancesAValider(jusquAu: string): Promise<number> {
  return withRetry(async () => {
    const { error, count } = await getClient()
      .from('echeances')
      .select('id', { count: 'exact', head: true })
      .in('statut', [...STATUTS_A_VALIDER])
      .lte('date_echeance', jusquAu);
    if (error !== null) {
      throw toErreur(error);
    }
    return count ?? 0;
  });
}

/** Date of the next pending due date from the given day, or null when none remains. */
export async function prochaineEcheanceAValider(aPartirDe: string): Promise<string | null> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('echeances')
      .select('date_echeance')
      .in('statut', [...STATUTS_A_VALIDER])
      .gte('date_echeance', aPartirDe)
      .order('date_echeance', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error !== null) {
      throw toErreur(error);
    }
    return data === null ? null : (data as { date_echeance: string }).date_echeance;
  });
}

/**
 * Rejected due dates and overdue planned arbitrages of the caller perimeter, each
 * resolved to its client. Feeds the dashboard "À traiter" list.
 */
export async function echeancesATraiter(aujourdHui: string): Promise<readonly EcheanceATraiter[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('echeances')
      .select('id,date_echeance,type,statut,simulation:simulations(client:clients(id,prenom,nom))')
      .or(`statut.eq.rejete,and(type.eq.arbitrage,statut.eq.planifie,date_echeance.lt.${aujourdHui})`)
      .order('date_echeance', { ascending: true });
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []).map((brut): EcheanceATraiter => {
      const ligne = brut as unknown as LigneATraiterBrute;
      return {
        id: ligne.id,
        date_echeance: ligne.date_echeance,
        type: ligne.type,
        statut: ligne.statut,
        client: ligne.simulation?.client ?? null,
      };
    });
  });
}

/** All echeances of a simulation, used to derive the validated contributions (RG-06). */
export async function toutesEcheances(simulationId: string): Promise<readonly Echeance[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('echeances')
      .select(COLONNES)
      .eq('simulation_id', simulationId)
      .order('date_echeance', { ascending: true });
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as Echeance[];
  });
}
